import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { User, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { useCurrentOrganization } from '@/hooks/use-current-organization';
import { useAlert } from '@/hooks/use-alert';
import { useToast } from '@/hooks/use-toast';
import type { ProjectTabProps } from '../types/project-details.types';
import { TicketSelectionCheckbox } from './TicketSelectionCheckbox';
import { TicketSelectAllCheckbox } from './TicketSelectAllCheckbox';
import { RightSidebar, SidebarButtonGroup, SidebarIconButton } from '@/components/ui/right-sidebar';
import { IconButtonFilter, IconButtonCancel, IconButtonSave } from '@/components/ui/icon-buttons';
import iconDeleteSvg from '@/assets/circle_delete.svg';

type ProjectTeamTabProps = ProjectTabProps & {
  searchTerm: string;
  canManageMembers?: boolean;
  onAddMemberReady?: (open: () => void) => void;
  onDeleteSelectedReady?: (removeSelected: () => void) => void;
  onSelectionChange?: (count: number) => void;
};

export const ProjectTeamTab: React.FC<ProjectTeamTabProps> = ({
  project,
  projectId,
  searchTerm,
  canManageMembers = true,
  onAddMemberReady,
  onDeleteSelectedReady,
  onSelectionChange,
}) => {
  const { t } = useTranslation(['pages.projectDetails']);
  const { confirm } = useAlert();
  const { toast } = useToast();
  const { organizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();
  const effectiveOrganizationId =
    organizationId ||
    (project as any)?.organizationId ||
    (project as any)?.organization_id ||
    null;
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedProjectMemberIds, setSelectedProjectMemberIds] = useState<Set<string>>(new Set());
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  
  // Member selection modal state
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [sortColumn, setSortColumn] = useState<'name' | 'email' | 'position' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [teamSortColumn, setTeamSortColumn] = useState<'name' | 'surname' | 'position' | null>(null);
  const [teamSortDirection, setTeamSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filterPositionIds, setFilterPositionIds] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { data: positionsResponse } = useQuery<{
    success: boolean;
    data: Array<{ id: string; name: string; displayName?: string }>;
  }>({
    queryKey: ["/api/positions", effectiveOrganizationId || "current"],
    queryFn: async () => {
      const url = effectiveOrganizationId
        ? `/api/positions?organizationId=${encodeURIComponent(effectiveOrganizationId)}`
        : "/api/positions";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!projectId,
    retry: false,
  });
  const positions = positionsResponse?.data || [];

  const resolvePositionDisplay = useCallback((positionId?: string | null, positionValue?: string | null, role?: string | null): string => {
    if ((role || '').trim().toLowerCase() === 'owner') return 'Owner';
    const canonicalPositionId = (positionId || '').trim();
    if (canonicalPositionId) {
      const positionByCanonicalId = positions.find((p: any) => p.id === canonicalPositionId);
      if (positionByCanonicalId) {
        return positionByCanonicalId.displayName || positionByCanonicalId.name || '';
      }
    }

    const position = (positionValue || '').trim();
    if (!position) return '';

    const positionById = positions.find((p: any) => p.id === position);
    if (positionById) return positionById.displayName || positionById.name || position;

    const positionByName = positions.find((p: any) =>
      String(p?.name || '').toLowerCase() === position.toLowerCase()
    );
    if (positionByName) return positionByName.displayName || positionByName.name || position;

    return position;
  }, [positions]);

  const getTeamIdentityKey = useCallback((member: {
    id?: string;
    entryType?: string | null;
    assigneeId?: string | null;
    invitationId?: string | null;
    linkedUserId?: string | null;
    compatibilityId?: string | null;
  }): string => {
    if (member.entryType === 'invitation' || member.invitationId) {
      return `invitation:${member.invitationId || member.compatibilityId || member.id || ''}`;
    }

    return `assignee:${member.assigneeId || member.linkedUserId || member.compatibilityId || member.id || ''}`;
  }, []);

  // Fetch project members using the new API
  const { data: projectMembersData, isLoading: isLoadingMembers } = useQuery<Array<{
    id: string;
    teamEntryId?: string | null;
    entryType?: 'assignee' | 'invitation';
    assigneeId?: string | null;
    invitationId?: string | null;
    linkedUserId?: string | null;
    compatibilityId?: string | null;
    email: string;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    positionId?: string | null;
    position: string;
    company: string;
    profilePhotoUrl?: string | null;
    role: string;
    assignedAt: string;
    // Backward compatibility with snake_case
    position_id?: string | null;
    first_name?: string;
    last_name?: string;
    display_name?: string;
    profile_photo_url?: string | null;
    assigned_at?: string;
  }>>({
    queryKey: ["/api/projects", projectId, "members"],
    queryFn: async () => {
      const response = await apiRequest(`/api/projects/${projectId}/members`);
      const data = await response.json();
      const members = data.data || [];
      // Ensure we always return an array
      return Array.isArray(members) ? members : [];
    },
    enabled: !!projectId
  });

  // Ensure projectMembers is always an array
  const projectMembers = Array.isArray(projectMembersData) ? projectMembersData : [];

  const { data: memberCandidatesResponse, isLoading: isLoadingCandidates } = useQuery<{
    success: boolean;
    data: Array<{
      id: string;
      teamEntryId?: string | null;
      entryType?: 'assignee' | 'invitation';
      assigneeId?: string | null;
      invitationId?: string | null;
      linkedUserId?: string | null;
      compatibilityId?: string | null;
      email?: string | null;
      firstName?: string | null;
      first_name?: string | null;
      lastName?: string | null;
      last_name?: string | null;
      displayName?: string | null;
      display_name?: string | null;
      positionId?: string | null;
      position_id?: string | null;
      position?: string | null;
      company?: string | null;
      invitationStatus?: 'none' | 'pending' | 'sent' | 'accepted' | 'expired' | null;
    }>;
  }>({
    queryKey: ["/api/projects", projectId, "member-candidates"],
    queryFn: async () => {
      const response = await apiRequest(`/api/projects/${projectId}/member-candidates`);
      return await response.json();
    },
    enabled: !!projectId && showAddMember,
    retry: false,
  });

  const memberCandidates = useMemo(() => {
    const entries = Array.isArray(memberCandidatesResponse?.data)
      ? memberCandidatesResponse.data
      : [];

    return entries.map((member) => {
      const firstName = member.firstName || member.first_name || '';
      const lastName = member.lastName || member.last_name || '';
      const displayName =
        member.displayName ||
        member.display_name ||
        `${firstName} ${lastName}`.trim() ||
        member.email ||
        '';

      return {
        ...member,
        firstName,
        lastName,
        displayName,
        positionId: member.positionId || member.position_id || null,
        position: member.position || '',
        company: member.company || '',
      };
    });
  }, [memberCandidatesResponse]);

  // Add member to project mutation
  // Accepts either { userId } for active contractors or { invitationId } for pending invitations
  const addMemberMutation = useMutation({
    mutationFn: async (payload: { userId?: string; assigneeId?: string; invitationId?: string; entryType?: 'assignee' | 'invitation' }) => {
      const response = await apiRequest(`/api/projects/${projectId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/projects", projectId, "members"] });
    },
    onError: (error: any) => {
      toast({
        title: t('team.addMemberError', 'Error'),
        description: error.message || t('team.addMemberErrorMessage', 'Failed to add member to project'),
        variant: 'destructive',
      });
    },
  });

  // Batch add members mutation
  const batchAddMembersMutation = useMutation({
    mutationFn: async (memberIds: string[]) => {
      const payloads = memberIds.map((memberId) => {
        const member = memberCandidates.find((candidate) => getTeamIdentityKey(candidate) === memberId);
        if (member?.entryType === 'invitation' && member.invitationId) {
          return { entryType: 'invitation' as const, invitationId: member.invitationId };
        }
        if (member?.assigneeId) {
          return { entryType: 'assignee' as const, assigneeId: member.assigneeId };
        }
        if (member?.linkedUserId) {
          return { userId: member.linkedUserId };
        }
        return { userId: memberId };
      });
      
      const results = await Promise.allSettled(
        payloads.map(payload => addMemberMutation.mutateAsync(payload))
      );
      const errors = results.filter(r => r.status === 'rejected');
      if (errors.length > 0) {
        throw new Error(`${errors.length} member(s) failed to add`);
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/projects", projectId, "members"] });
      setShowAddMember(false);
      setSelectedMemberIds(new Set());
      setMemberSearchTerm('');
      toast({
        title: t('team.membersAdded', 'Success'),
        description: t('team.membersAddedMessage', 'Members added to project successfully'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('team.addMemberError', 'Error'),
        description: error.message || t('team.addMemberErrorMessage', 'Failed to add some members'),
        variant: 'destructive',
      });
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest(`/api/projects/${projectId}/members/${userId}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error(await response.text());
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/projects", projectId, "members"] });
    }
  });



  // Get project member IDs for comparison
  const projectMemberIds = useMemo(() => {
    return new Set(projectMembers.map((member) => getTeamIdentityKey(member)));
  }, [getTeamIdentityKey, projectMembers]);

  // Filter and sort organization members
  const filteredAndSortedMembers = useMemo(() => {
    if (!memberCandidates) return [];
    
    let filtered = memberCandidates.filter((member) => {
      if (!memberSearchTerm.trim()) return true;
      const searchLower = memberSearchTerm.toLowerCase();
      const firstName = (member.firstName || '').toLowerCase();
      const lastName = (member.lastName || '').toLowerCase();
      const displayName = (member.displayName || '').toLowerCase();
      const email = (member.email || '').toLowerCase();
      const position = resolvePositionDisplay(member.positionId, member.position).toLowerCase();
      
      return firstName.includes(searchLower) ||
             lastName.includes(searchLower) ||
             displayName.includes(searchLower) ||
             email.includes(searchLower) ||
             position.includes(searchLower);
    });

    // Sort
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: string = '';
        let bValue: string = '';
        
        switch (sortColumn) {
          case 'name':
            aValue = (a.displayName || `${a.firstName} ${a.lastName}` || a.email || '').toLowerCase();
            bValue = (b.displayName || `${b.firstName} ${b.lastName}` || b.email || '').toLowerCase();
            break;
          case 'email':
            aValue = (a.email || '').toLowerCase();
            bValue = (b.email || '').toLowerCase();
            break;
          case 'position':
            aValue = resolvePositionDisplay(a.positionId, a.position).toLowerCase();
            bValue = resolvePositionDisplay(b.positionId, b.position).toLowerCase();
            break;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [memberCandidates, memberSearchTerm, sortColumn, sortDirection, resolvePositionDisplay]);

  const handleSort = (column: 'name' | 'email' | 'position') => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleMemberToggle = (memberId: string, isInProject: boolean) => {
    if (!canManageMembers) return;
    if (isInProject) return; // Don't allow unchecking existing members
    
    setSelectedMemberIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  const handleAddSelectedMembers = () => {
    if (!canManageMembers) return;
    const userIdsToAdd = Array.from(selectedMemberIds);
    if (userIdsToAdd.length === 0) return;
    
    batchAddMembersMutation.mutate(userIdsToAdd);
  };

  const handleAddMember = useCallback(() => {
    setShowAddMember(true);
    setSelectedMemberIds(new Set());
    setMemberSearchTerm('');
    setSortColumn(null);
    setSortDirection('asc');
  }, []);

  const handleProjectMemberToggle = useCallback((memberId: string) => {
    if (!canManageMembers) return;
    setSelectedProjectMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  }, [canManageMembers]);

  const handleProjectMembersToggleAll = useCallback((memberIds: string[]) => {
    if (!canManageMembers) return;
    setSelectedProjectMemberIds((prev) => {
      const allSelected = memberIds.every((id) => prev.has(id));
      if (allSelected) {
        return new Set();
      }
      return new Set(memberIds);
    });
  }, [canManageMembers]);

  const handleDeleteSelectedMembers = useCallback(async () => {
    if (!canManageMembers) return;
    if (isDeletingSelected || selectedProjectMemberIds.size === 0) return;

    const result = await confirm({
      message: t('team.deleteSelectedConfirm'),
      variant: 'destructive',
    });
    if (!result) return;

    const memberIds = Array.from(selectedProjectMemberIds);
    try {
      setIsDeletingSelected(true);
      await Promise.all(memberIds.map((memberId) => removeMemberMutation.mutateAsync(memberId)));
      setSelectedProjectMemberIds(new Set());
    } finally {
      setIsDeletingSelected(false);
    }
  }, [canManageMembers, confirm, removeMemberMutation, selectedProjectMemberIds, t, isDeletingSelected]);

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'user': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin': return t('team.roles.admin');
      case 'manager': return t('team.roles.manager');
      case 'user': return t('team.roles.user');
      default: return role;
    }
  };

  useEffect(() => {
    if (!onAddMemberReady) return;
    onAddMemberReady(canManageMembers ? () => handleAddMember() : () => {});
  }, [canManageMembers, handleAddMember, onAddMemberReady]);

  useEffect(() => {
    if (!onDeleteSelectedReady) return;
    onDeleteSelectedReady(canManageMembers ? () => handleDeleteSelectedMembers() : () => {});
  }, [canManageMembers, handleDeleteSelectedMembers, onDeleteSelectedReady]);

  useEffect(() => {
    if (!onSelectionChange) return;
    onSelectionChange(selectedProjectMemberIds.size);
  }, [onSelectionChange, selectedProjectMemberIds.size]);

  useEffect(() => {
    setSelectedProjectMemberIds(new Set());
  }, [projectMembers]);

  const handleTeamSort = (column: 'name' | 'surname' | 'position') => {
    if (teamSortColumn === column) {
      setTeamSortDirection(teamSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTeamSortColumn(column);
      setTeamSortDirection('asc');
    }
  };

  const getTeamSortValue = (member: any, column: 'name' | 'surname' | 'position') => {
    const firstName = (member.firstName || member.first_name || '').toLowerCase();
    const lastName = (member.lastName || member.last_name || '').toLowerCase();
    const displayName = (member.displayName || member.display_name || '').toLowerCase();
    if (column === 'name') {
      return displayName || firstName;
    }
    if (column === 'surname') {
      return lastName;
    }
    return resolvePositionDisplay(member.positionId, member.position, member.role).toLowerCase();
  };

  // Filter members based on search term
  const searchFilteredMembers = projectMembers.filter(member => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    const firstName = (member.firstName || member.first_name || '').toLowerCase();
    const lastName = (member.lastName || member.last_name || '').toLowerCase();
    const displayName = (member.displayName || member.display_name || '').toLowerCase();
    const email = (member.email || '').toLowerCase();
    const position = resolvePositionDisplay(member.positionId, member.position, member.role).toLowerCase();
    const company = (member.company || '').toLowerCase();
    
    return firstName.includes(searchLower) ||
           lastName.includes(searchLower) ||
           displayName.includes(searchLower) ||
           email.includes(searchLower) ||
           position.includes(searchLower) ||
           company.includes(searchLower);
  });

  // Filter by position (organization role) - sidebar filter
  const filteredMembers = useMemo(() => {
    if (filterPositionIds.length === 0) return searchFilteredMembers;
    return searchFilteredMembers.filter(member => {
      const posId = (member.positionId || '').trim();
      if (posId && filterPositionIds.includes(posId)) return true;
      const posRaw = (member.position || '').trim();
      const posResolved = resolvePositionDisplay(member.positionId, member.position, member.role).trim();
      if (!posRaw && !posResolved && !posId) return false;
      return filterPositionIds.some(selectedId => {
        const posItem = positions.find((p: any) => p.id === selectedId);
        if (!posItem) return false;
        const positionName = (posItem.name || '').trim();
        const positionDisplay = (posItem.displayName || posItem.name || '').trim();

        return (
          selectedId === posRaw ||
          positionName === posRaw ||
          positionDisplay === posRaw ||
          selectedId === posId ||
          selectedId === posResolved ||
          positionName === posResolved ||
          positionDisplay === posResolved
        );
      });
    });
  }, [searchFilteredMembers, filterPositionIds, positions, resolvePositionDisplay]);

  const sortedMembers = useMemo(() => {
    if (!teamSortColumn) return filteredMembers;
    const sorted = [...filteredMembers].sort((a, b) => {
      const aValue = getTeamSortValue(a, teamSortColumn);
      const bValue = getTeamSortValue(b, teamSortColumn);
      return teamSortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    return sorted;
  }, [filteredMembers, teamSortColumn, teamSortDirection]);

  if (isLoadingMembers) {
    return (
      <div className="p-6 text-center text-slate-500">
        <p>Loading team...</p>
      </div>
    );
  }

  const hasSelection = selectedProjectMemberIds.size > 0;
  const positionFilterOptions = positions.map((p: any) => ({
    value: p.id,
    label: p.displayName || p.name,
  }));

  return (
    <div className="p-4 pt-2 -mt-[8px] h-full flex flex-col min-h-0">
      <div className="flex gap-4 max-h-full min-h-0">
        {/* Team Members Section */}
        <div className="flex-1 bg-[#DDDDDD] rounded-[24px] p-[32px] flex flex-col min-h-0 max-h-full">
          {sortedMembers.length > 0 ? (
          <div className="bg-white border border-slate-200 overflow-hidden rounded-none shadow-none flex flex-col min-h-0 max-h-full">
            <div className="overflow-auto min-h-0 pb-4">
              <table className="min-w-full">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-slate-200">
                    <th 
                      className="px-3 py-3 text-left w-12"
                      style={{ 
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#000000' 
                      }}
                    >
                      <TicketSelectAllCheckbox
                        isAllSelected={sortedMembers.length > 0 && sortedMembers.every((member) => selectedProjectMemberIds.has(member.id))}
                        isIndeterminate={selectedProjectMemberIds.size > 0 && !sortedMembers.every((member) => selectedProjectMemberIds.has(member.id))}
                        onToggleAll={() => handleProjectMembersToggleAll(sortedMembers.map((member) => member.id))}
                        disabled={!canManageMembers}
                      />
                    </th>
                    <th 
                      className="px-3 py-3 text-left w-[220px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleTeamSort('name')}
                      style={{ 
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#000000' 
                      }}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="truncate">{t('team.table.name') || 'Name'}</span>
                        {teamSortColumn === 'name' ? (
                          teamSortDirection === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0" /> : <ArrowDown className="h-3 w-3 flex-shrink-0" />
                        ) : null}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-left w-[220px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleTeamSort('surname')}
                      style={{ 
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#000000' 
                      }}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="truncate">{t('team.table.surname') || 'Surname'}</span>
                        {teamSortColumn === 'surname' ? (
                          teamSortDirection === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0" /> : <ArrowDown className="h-3 w-3 flex-shrink-0" />
                        ) : null}
                      </div>
                    </th>
                    <th 
                      className="px-3 py-3 text-left w-[300px] max-w-[300px]"
                      style={{ 
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#000000' 
                      }}
                    >
                      <span className="truncate block">{t('team.table.email') || 'Email'}</span>
                    </th>
                    <th 
                      className="px-3 py-3 text-left w-[220px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleTeamSort('position')}
                      style={{ 
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#000000' 
                      }}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="truncate">{t('team.table.position') || 'Position'}</span>
                        {teamSortColumn === 'position' ? (
                          teamSortDirection === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0" /> : <ArrowDown className="h-3 w-3 flex-shrink-0" />
                        ) : null}
                      </div>
                    </th>
                    <th className="px-3 py-3"></th>
                  </tr>
                </thead>
                <tbody>
            {sortedMembers.map(member => {
              // Use camelCase with fallback to snake_case for backward compatibility
              const firstName = member.firstName || member.first_name || '';
              const lastName = member.lastName || member.last_name || '';
              const positionDisplay = resolvePositionDisplay(member.positionId, member.position, member.role);
              
                    return (
                      <tr 
                        key={member.id}
                        className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
                        style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
                      >
                        <td className="px-3 py-2 whitespace-nowrap w-12">
                          <TicketSelectionCheckbox
                            ticketId={member.id}
                            isSelected={selectedProjectMemberIds.has(member.id)}
                            onToggle={handleProjectMemberToggle}
                            disabled={!canManageMembers || removeMemberMutation.isPending}
                          />
                        </td>
                        <td 
                          className="px-3 py-2 whitespace-nowrap w-[220px]"
                          style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '14px', 
                            fontWeight: 'normal', 
                            color: '#000000' 
                          }}
                        >
                          {firstName || '-'}
                        </td>
                        <td 
                          className="px-3 py-2 whitespace-nowrap w-[220px]"
                          style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '14px', 
                            fontWeight: 'normal', 
                            color: '#000000' 
                          }}
                        >
                          {lastName || '-'}
                        </td>
                        <td className="px-3 py-2 w-[300px] max-w-[200px]">
                          {(member.email || '').trim() ? (
                            <a
                              href={`mailto:${member.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-sm text-black hover:underline truncate block"
                              title={member.email}
                            >
                              {member.email}
                            </a>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td 
                          className="px-3 py-2 whitespace-nowrap text-left w-[220px]"
                          style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '14px', 
                            fontWeight: 'normal', 
                            color: '#000000' 
                          }}
                        >
                          {positionDisplay || '-'}
                        </td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : searchTerm || filterPositionIds.length > 0 ? (
          <div className="text-center py-8 text-slate-500">
            <User className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-sm">No team members found matching your search or filter</p>
            <p className="text-xs mt-1">Try a different search term or clear the filter</p>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <User className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-sm">No team members assigned to this project</p>
            <p className="text-xs mt-1">Add members to collaborate on this project</p>
          </div>
        )}
        </div>

        {/* Right Sidebar Menu */}
        <RightSidebar>
          <SidebarButtonGroup label={t('plans.filter', 'Filter')} hideLabel>
            <button
              type="button"
              className="relative inline-block"
              onClick={() => setIsFilterOpen(true)}
            >
              <IconButtonFilter
                size={36}
                title={t('team.filterByPosition', 'Filter by position')}
              />
              {filterPositionIds.length > 0 && (
                <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-500 rounded-full"></span>
              )}
            </button>
            <Dialog open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <DialogContent className="w-64 p-0 overflow-hidden bg-[#E8E8E8] rounded-2xl border-0 shadow-md">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-300 bg-[#E8E8E8] rounded-t-2xl">
                  <h3 className="font-semibold text-sm text-black">{t('team.filterByPosition', 'Filter by position')}</h3>
                  {filterPositionIds.length > 0 && (
                    <button
                      onClick={() => setFilterPositionIds([])}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {t('team.clearFilter', 'Clear filter')}
                    </button>
                  )}
                </div>
                <div className="p-4">
                  <fieldset className="border border-gray-300 rounded-lg p-4 bg-white">
                    <legend className="px-2 text-sm font-medium text-black">{t('team.table.position', 'Position')}</legend>
                    <div className="space-y-2 mt-2">
                      {positionFilterOptions.length > 0 ? (
                        positionFilterOptions.map(({ value, label }) => (
                          <div key={value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`position-${value}`}
                              checked={filterPositionIds.includes(value)}
                              onCheckedChange={(checked) => {
                                setFilterPositionIds(prev =>
                                  checked ? [...prev, value] : prev.filter(id => id !== value)
                                );
                              }}
                            />
                            <label
                              htmlFor={`position-${value}`}
                              className="text-sm text-slate-700 cursor-pointer"
                            >
                              {label}
                            </label>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-slate-500">{t('team.noPositionsAvailable', 'No positions available')}</div>
                      )}
                    </div>
                  </fieldset>
                </div>
              </DialogContent>
            </Dialog>
          </SidebarButtonGroup>
          <SidebarButtonGroup label={t('common:delete')} hideLabel>
            <SidebarIconButton
              icon={iconDeleteSvg}
              alt="Delete"
              onClick={handleDeleteSelectedMembers}
              disabled={!canManageMembers || !hasSelection}
              isLoading={isDeletingSelected}
              title={t('team.deleteSelected')}
            />
          </SidebarButtonGroup>
        </RightSidebar>
      </div>

      {/* Add Member Modal */}
      <Dialog open={showAddMember} onOpenChange={(open) => {
        setShowAddMember(open);
        if (!open) {
          setSelectedMemberIds(new Set());
          setMemberSearchTerm('');
          setSortColumn(null);
          setSortDirection('asc');
        }
      }}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col bg-[#DDDDDD] rounded-[24px] [&>button]:hidden">
          <DialogHeader className="flex-row items-center justify-between">
            <DialogTitle>{t('team.addMember', 'Add Team Members')}</DialogTitle>
            <div className="flex items-center gap-2">
              <IconButtonCancel
                onClick={() => {
                  setShowAddMember(false);
                  setSelectedMemberIds(new Set());
                  setMemberSearchTerm('');
                }}
                size={30}
                title={t('alerts.cancel', 'Cancel')}
              />
              <IconButtonSave
                onClick={handleAddSelectedMembers}
                disabled={!canManageMembers || selectedMemberIds.size === 0 || batchAddMembersMutation.isPending}
                size={30}
                title={batchAddMembersMutation.isPending 
                  ? t('team.adding', 'Adding...')
                  : t('team.addSelected', `Add Selected (${selectedMemberIds.size})`)}
              />
            </div>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* Search */}
            <div>
              <Input
                type="text"
                placeholder={t('team.searchMembers', 'Search members by name, email, or position...')}
                value={memberSearchTerm}
                onChange={(e) => setMemberSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Members Table */}
            <div className="flex-1 overflow-auto border rounded-lg bg-white">
              {isLoadingCandidates ? (
                <div className="p-8 text-center text-slate-500">
                  <p>{t('team.loadingMembers', 'Loading members...')}</p>
                </div>
              ) : filteredAndSortedMembers.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <User className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-sm">
                    {memberSearchTerm 
                      ? t('team.noMembersFound', 'No members found matching your search')
                      : t('team.noMembersAvailable', 'No members available')}
                  </p>
                </div>
              ) : (
                <table className="min-w-full">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="border-b border-slate-200">
                      <th className="px-4 py-3 text-left w-12">
                        <Checkbox
                          checked={filteredAndSortedMembers.length > 0 && 
                                   filteredAndSortedMembers.every((member) => {
                                     const identityKey = getTeamIdentityKey(member);
                                     return projectMemberIds.has(identityKey) || selectedMemberIds.has(identityKey);
                                   })}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              // Select all members that are not already in the project
                              const availableIds = filteredAndSortedMembers
                                .filter((member) => !projectMemberIds.has(getTeamIdentityKey(member)))
                                .map((member) => getTeamIdentityKey(member));
                              setSelectedMemberIds(new Set(availableIds));
                            } else {
                              setSelectedMemberIds(new Set());
                            }
                          }}
                          disabled={!canManageMembers || filteredAndSortedMembers.every((member) => projectMemberIds.has(getTeamIdentityKey(member)))}
                        />
                      </th>
                      <th 
                        className="px-4 py-3 text-left cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center space-x-2">
                          <span style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: '#000000' 
                          }}>
                            {t('team.table.name', 'Name')}
                          </span>
                          {sortColumn === 'name' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-slate-400" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('email')}
                      >
                        <div className="flex items-center space-x-2">
                          <span style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: '#000000' 
                          }}>
                            {t('team.table.email', 'Email')}
                          </span>
                          {sortColumn === 'email' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-slate-400" />
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort('position')}
                      >
                        <div className="flex items-center space-x-2">
                          <span style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: '#000000' 
                          }}>
                            {t('team.table.position', 'Position')}
                          </span>
                          {sortColumn === 'position' ? (
                            sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-slate-400" />
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedMembers.map((member) => {
                      const identityKey = getTeamIdentityKey(member);
                      const isInProject = projectMemberIds.has(identityKey);
                      const isSelected = selectedMemberIds.has(identityKey);
                      const displayName = member.displayName || `${member.firstName} ${member.lastName}`.trim() || member.email;
                      
                      return (
                        <tr 
                          key={identityKey}
                          className={`border-b border-slate-200 hover:bg-slate-50 transition-colors ${
                            isInProject ? 'bg-slate-100 opacity-60' : ''
                          }`}
                        >
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={isInProject || isSelected}
                              onCheckedChange={() => handleMemberToggle(identityKey, isInProject)}
                              disabled={!canManageMembers || isInProject}
                            />
                          </td>
                          <td className="px-4 py-3" style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '14px', 
                            color: '#000000' 
                          }}>
                            {displayName}
                          </td>
                          <td className="px-4 py-3" style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '14px', 
                            color: '#000000' 
                          }}>
                            {member.email}
                          </td>
                          <td className="px-4 py-3" style={{ 
                            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", 
                            fontSize: '14px', 
                            color: '#000000' 
                          }}>
                            {resolvePositionDisplay(member.positionId, member.position) || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};
